import {
  Schema,
  schema
} from 'normalizr';
import { SchemaMapping } from './entities';

/**
 * Base interface for entity input from API responses
 * Used for truly dynamic API fields that vary by entity type
 */
export interface BaseEntityInput {
  id?: string | number | null;
  // Use unknown for truly dynamic API fields that vary by entity type
  [key: string]: string | number | boolean | null | undefined | unknown[] | unknown;
}

/**
 * A factory function that generates normalizr schema with full TypeScript support
 * @param key - A string that indicates the key to be used to identify the Entity in (de)normalization
 * @param mapping - A mapping of options to let this function know how to handle the Entity's properties
 * @param mapping.schema - The specific schema mapping that specifies the structure of the response from the API
 * @param mapping.isId - Optional. Only used for core elements fields, identifies if we should expect back the possibility of just an id
 * @param mapping.idField - Optional. Defaults to `{mapping.field}Id` in case we're looking for the idField
 * @param mapping.originalField - Optional. Used to re-map an Entity property to its original response field (e.g., size -> sizeProperty)
 * @param mapping.isComplexUnit - Optional. Used to indicate that the field will have an array of units as the response
 * @param mapping.isList - Optional. Used to indicate that the field will be an array of entities
 * @returns A normalizr Entity schema with proper TypeScript typing
 */
const baseSchema = (key: string, mapping: Record<string, SchemaMapping> = {}): Schema =>
  new schema.Entity(
    key,
    Object.entries(mapping).reduce(
      (structure, [field, { schema: entitySchema }]) =>
        entitySchema ? { ...structure, [field]: entitySchema } : structure,
      {} as Record<string, Schema>
    ),
    {
      // Using this function to convert the id for every Entity to ensure consistency within the app
      idAttribute: (entity: BaseEntityInput): string | number | null =>
        entity.id != null ? `${entity.id}` : entity.id,

      // processStrategy is used to perform actions before normalization.
      // We're using the model schemas to configure how this function will run for each model based on its properties
      processStrategy: (entity: BaseEntityInput): BaseEntityInput & { _normalized: boolean } =>
        Object.entries(mapping).reduce(
          (
            output: BaseEntityInput & { _normalized?: boolean },
            [field, { originalField, idField, isId, isList, isComplexUnit }]: [string, SchemaMapping]
          ) => {
            const entityField = entity[originalField || field];
            const entityIdField = entity[idField || `${originalField || field}Id`];

            if (isId) {
              // If the isId param is present, we're assuming we're getting back ids from the server
              if (entityField == null && entityIdField != null) {
                if (isList && Array.isArray(entityIdField)) {
                  output[field] = entityIdField.map((id: string | number) => `${id}`);
                } else {
                  output[field] = `${entityIdField}`;
                }
              } else {
                output[field] = entityField;
              }
            } else if (isComplexUnit) {
              // If the isComplexUnit is present, we're assuming that the server will send back an array of units
              if (Array.isArray(entityField)) {
                output[field] = entityField.map((unit: BaseEntityInput) => ({
                  id: unit.id != null ? `${unit.id}` : unit.id,
                  ...Object.fromEntries(
                    Object.entries(unit).filter(([key]) => key !== 'id').map(([key, value]) => [key, value])
                  ),
                }));
              } else {
                output[field] = [];
              }
            } else if (originalField) {
              // If idField is present and not nil, use it; otherwise, use originalField
              output[field] = entityIdField != null
                ? entityIdField
                : entity[originalField];
            }
            return output;
          },
          // Ensure id is always a string and _normalized is set
          {
            ...entity,
            id: entity.id != null ? `${entity.id}` : entity.id,
            _normalized: true,
          } as BaseEntityInput & { _normalized: boolean }
        ),
    }
  );

// Alias for template-based schemas (same function, different name for clarity)
const templateSchema = baseSchema;

export { baseSchema, templateSchema };